import { FC, useCallback, useEffect, useState } from 'react'
import {
  Button,
  CreateButton,
  Datagrid,
  FilterList,
  FilterListItem,
  FunctionField,
  List,
  TextField,
  TopToolbar,
  useTranslate,
} from 'react-admin'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getPerPage } from '../../utils/usePerPage'
import { getJson, getUrl } from '../../providers/data'
import { EnabledChip, FilterAside, MarketplaceChip } from '../../components'
import { xxllncColor } from '../../layout/theme/color'
import { Chip } from '@mui/material'
import { WorkflowGenerationRequestOut, WorkflowOut } from '../../types/apiRequests'
import AiGenerateButton from './AiGenerateButton'
import AiGenerateDialog from './AiGenerateDialog'

const EUR_4 = new Intl.NumberFormat('nl-NL', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
})

const WorkflowListActions: FC = () => {
  const navigate = useNavigate()
  const translate = useTranslate()
  return (
    <TopToolbar>
      <Button
        label={translate('workflow.actions.executions')}
        onClick={() => navigate('/workflow_execution')}
      >
        <PlayCircleOutlineIcon />
      </Button>
      <AiGenerateButton />
      <CreateButton />
    </TopToolbar>
  )
}

const PrefillFromRequest: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const requestId = searchParams.get('ai_request')
  const [prefill, setPrefill] = useState<{ prompt: string; summary: string } | null>(null)

  const clearParam = useCallback(() => {
    const next = new URLSearchParams(searchParams)
    next.delete('ai_request')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  useEffect(() => {
    if (!requestId) {
      setPrefill(null)
      return
    }
    let cancelled = false
    void getJson<WorkflowGenerationRequestOut>(
      `${getUrl()}/workflow_generation_request/${requestId}`,
    )
      .then((req) => {
        if (cancelled) return
        // Only open the generator for approved, not-yet-built requests; clear the param for any other state.
        if (req.status !== 'approved' || req.created_workflow_id) {
          clearParam()
          return
        }
        setPrefill({ prompt: req.prompt, summary: req.summary ?? '' })
      })
      .catch(() => {
        // Fetch failed — clear the dangling URL param.
        if (!cancelled) clearParam()
      })
    return () => {
      cancelled = true
    }
  }, [requestId, clearParam])

  const handleClose = () => {
    setPrefill(null)
    clearParam()
  }

  return (
    <AiGenerateDialog
      open={!!requestId && prefill !== null}
      onClose={handleClose}
      initialPrompt={prefill?.prompt ?? ''}
      initialSummary={prefill?.summary || undefined}
      requestId={requestId ?? undefined}
    />
  )
}

const WorkflowFilters: FC = () => {
  const translate = useTranslate()
  return (
    <FilterAside>
      <FilterList label={translate('filters.status')} icon={<PowerSettingsNewIcon />}>
        <FilterListItem label="filters.active" value={{ enabled: true }} />
        <FilterListItem label="filters.inactive" value={{ enabled: false }} />
      </FilterList>

      <FilterList label={translate('workflow.filters.trigger')} icon={<PlayArrowIcon />}>
        {/* eslint-disable camelcase */}
        <FilterListItem label="workflow.filters.triggerManual" value={{ trigger_type: 'manual' }} />
        <FilterListItem label="workflow.filters.triggerCron" value={{ trigger_type: 'cron' }} />
        <FilterListItem label="workflow.filters.triggerWebhook" value={{ trigger_type: 'webhook' }} />
        <FilterListItem label="workflow.filters.triggerMcp" value={{ trigger_type: 'mcp' }} />
        {/* eslint-enable camelcase */}
      </FilterList>
    </FilterAside>
  )
}

const WorkflowList: FC = () => {
  const translate = useTranslate()
  return (
    <>
      <List
        exporter={false}
        perPage={getPerPage()}
        sort={{ field: 'lastModified', order: 'DESC' }}
        actions={<WorkflowListActions />}
        aside={<WorkflowFilters />}
        title={translate('menu.workflows')}
      >
        <Datagrid bulkActionButtons={false} rowClick="show">
          <TextField source="display_name" />
          <FunctionField label="data.enabled" render={() => <EnabledChip />} />
          <FunctionField label="data.marketplaceVisible" render={() => <MarketplaceChip />} />
          <FunctionField<WorkflowOut>
            label="workflow.list.triggerType"
            render={(record) =>
              record.trigger_type ? (
                <Chip
                  label={record.trigger_type}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    fontSize: 11,
                    height: 22,
                    border: 'none',
                    bgcolor: xxllncColor.opDark10,
                    color: xxllncColor.dark100,
                    textTransform: 'capitalize',
                  }}
                />
              ) : (
                '—'
              )
            }
          />
          <FunctionField<WorkflowOut>
            label="workflow.list.cost"
            render={(record) => {
              if (record.cost_per_workflow_execution != null) {
                return EUR_4.format(record.cost_per_workflow_execution)
              }
              if (record.effective_cost_per_workflow_execution != null) {
                return `${EUR_4.format(record.effective_cost_per_workflow_execution)} ${translate(
                  'workflow.list.costPortalDefault',
                )}`
              }
              return translate('workflow.list.costNotSet')
            }}
          />
          <FunctionField<WorkflowOut>
            label="workflow.list.links"
            render={(record) => String(record.link_count ?? 0)}
          />
          <FunctionField<WorkflowOut>
            label="workflow.list.executions24h"
            render={(record) => String(record.executions_24h ?? 0)}
          />
        </Datagrid>
      </List>
      <PrefillFromRequest />
    </>
  )
}

export default WorkflowList
